import { AlertDefaults, AlertType, MessageSettings, PollOptionModel } from 'src/app/hickory-api';
import { AlertModelService } from '..';
import { AlertMessageHelper } from '../helpers/alert-message-helper';

export class AlertPollHelper {
  static readonly MIN_OPTIONS = 2;
  static readonly MAX_OPTIONS = 5;

  static initPoll(message?: MessageSettings): MessageSettings {
    if (!message) message = {};
    AlertMessageHelper.initMessage(message);
    if (!message.poll) message.poll = {};
    if (!message.poll.options) message.poll.options = [];
    return message;
  }

  static postProcess(service: AlertModelService, alert: AlertDefaults = service.alertSettings) {
    if (alert.alertType !== AlertType.Poll) return;
    if (!alert.message?.poll) alert.message = this.initPoll(alert.message);
    const options = alert.message.poll.options;
    while (options.length < AlertPollHelper.MIN_OPTIONS) this.addPollOption(service, options);
  }

  static preparePreview(service: AlertModelService, preview: AlertDefaults) {
    // only include non-empty poll options
    if (preview?.message?.poll?.options) {
      preview.message.poll.options = this.validOptions(service, preview);
    }

    // clear workflow if not enabled
    if (!service.usePollWorkflow) preview.workflow = undefined;
    if (!service.useVoicePriority) preview.prioritySettings = undefined;
  }

  static validateQuestion(service: AlertModelService, alert: AlertDefaults = service.alertSettings): string[] {
    const poll = alert.message?.poll;
    const errors = [];
    if (!poll?.question) errors.push('Alert.Poll.FinishPollQuestion');
    return errors;
  }

  static validateOptions(service: AlertModelService, alert: AlertDefaults = service.alertSettings): string[] {
    const errors = [];
    const length = this.validOptions(service, alert)?.length || 0;
    if (length < this.MIN_OPTIONS) errors.push('Alert.Poll.FinishPollOptions');
    if (length > this.MAX_OPTIONS) errors.push('Alert.Poll.AddOptionDisabled');
    return errors;
  }

  static validateLength(service: AlertModelService, alert: AlertDefaults = service.alertSettings): string[] {
    const errors = [];
    if (alert.channels?.textMessage?.send !== true) return errors;
    const length = this.generateSmsPreviewText(service, alert)?.length ?? 0;
    if (length > this.SMS_CHARACTER_LIMIT) errors.push('Alert.Poll.SmsMessageTooLong');
    return errors;
  }

  static readonly SMS_CHARACTER_LIMIT: number = 1500; // this matches our backend limit for SMS and also matches legacy Hickory UI behavior
  static readonly SMS_KEYWORD_EXAMPLE: string = 'WXYZ'; // for generating sample SMS messages
  static generateSmsPreviewText(service: AlertModelService, alert: AlertDefaults = service.alertSettings): string {
    const poll = alert.message?.poll;
    let message = poll?.question ?? '';
    poll?.options?.filter(o => !!o.description)?.forEach((option, index) => {
      if (message) message += '\r\n';
      message += `Reply ${this.SMS_KEYWORD_EXAMPLE} ${option.selectionChoice ?? (index - 1)} for ${option.description}`;
    });

    return message;
  }

  static truncateSmsPreviewText(message: string): string {
    return message.substring(0, this.SMS_CHARACTER_LIMIT - 1);
  }

  static validate(service: AlertModelService, alert: AlertDefaults = service.alertSettings): string[] {
    return this.validateQuestion(service, alert).concat(this.validateOptions(service, alert)).concat(this.validateLength(service, alert));
  }

  static validOptions(service: AlertModelService, alert: AlertDefaults = service.alertSettings): PollOptionModel[] {
    const poll = alert.message?.poll;
    if (!poll?.options?.length) return null;
    return poll?.options?.filter(o => !!o.description)?.map( (o, i) => { o.selectionChoice = i + 1; return o; });
  }

  static addPollOption(service: AlertModelService, options: PollOptionModel[] = service.alertPoll?.options) {
    if (options && options.length < AlertPollHelper.MAX_OPTIONS) {
      const sequence = options.length + 1;
      const option: PollOptionModel = { description: '', selectionChoice: sequence };
      options.push(option);
    }
  }
}
